/*
    Компонент для вывода информации по email
    Обогащает полученные в props данные емейла данными о компании с помощью /findByEmail/company,
    по готовности данных выполняет readyCallback, чтобы сообщить родителю о готовности

    Меняет отображение и часть логики в зависимости от того, где выводится
    (suggestion или clean)

    Заново запускает обогащение данных при их изменении.
*/

import ApiRequest from "../../../api-request";
import urls from "../../../urls";
import utils from "../../../utils";
import cookies from "../../../cookies";

const { settings } = window.DA;
const serviceUrl = urls.api.demoProxy.getUrl();
const token = settings.API_KEY;
const CSRF_TOKEN = cookies.get("csrftoken");

const FNS_DOESNT_NOW = "Данных нет в налоговой";
const EMAIL_TYPES = {
    ROLE: "Ролевой",
    CORPORATE: "Корпоративный",
    PERSONAL: "Личный — не связан с доменом компании",
    DISPOSABLE: "Одноразовый — не связан с доменом компании",
};

const template = /* html */`
<div class="flex-column items-start flex-row-sm gap-2 gap-sm-4 max-w-12">
    <div class="grid results-block results-block--left items-start gap-sm-1">
        <span
            class="mb-1 mb-sm-0 font-roboto-bold grid-col-span-sm-2"
        >
            [[ type === "suggestion" ? 'Емейл по частям' : 'Результат разбора' ]]
        </span>
        <template v-if="type === 'clean'">
            <span class="color-gray">E-mail</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="local">
                [[ suggestion.email ]]
            </span>
        </template>
        <span class="color-gray">Ящик</span>
        <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="local">
            [[ (type === "suggestion" ? suggestion.data.local : suggestion.local) || '—' ]]
        </span>
        <span class="color-gray">Домен</span>
        <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="domain">
            [[ (type === "suggestion" ? suggestion.data.domain : suggestion.domain) || '—' ]]
        </span>
        <template v-if="role">
            <span class="color-gray">Тип</span>
            <div class="mb-1 mb-sm-0 mt-0_25 mt-sm-0">
                <span data-ref="role">
                    [[ role ]]
                </span>
                <span class="block" v-if="type === 'suggestion'">
                    Определяем отдельно,
                </span>
                <span class="block" v-if="type === 'suggestion'">
                    через <a href="/api/clean/email/">API «Стандартизации»</a>
                </span>
            </div>
        </template>
    </div>
    <transition name="collapse">
        <div class="grid results-block items-start gap-sm-1" v-if="company" data-ref="additional">
            <span
                class="mb-1 mb-sm-0 font-roboto-bold grid-col-span-sm-2"
            >
                Дополнительная информация
            </span>
            <span class="color-gray">Юрлицо</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="name">
                [[ company.name ]]
            </span>
            <span class="color-gray">ИНН</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="inn">
                [[ company.inn ]]
            </span>
            <span class="color-gray">ОГРН</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="ogrn">
                [[ company.ogrn ]]
            </span>
            <span class="color-gray">Доходы</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="income">
                [[ company.income ]]
            </span>
            <span class="color-gray">Сотрудников</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="employee_count">
                [[ company.employee_count ]]
            </span>
            <span class="color-gray">Город рег.</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="city">
                [[ company.city ]]
            </span>
            <span class="color-gray">Час. пояс</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="timezone">
                [[ company.timezone ]]
            </span>
            <span class="color-gray">Отрасль</span>
            <span class="mb-1 mb-sm-0 mt-0_25 mt-sm-0" data-ref="okved" v-html="company.okved" />
            <span
                class="mb-1 mb-sm-0 grid-col-span-sm-2"
                data-ref="api_link"
            >
                Информацию о юрлице-владельце почты определяем отдельно,
                <span class="block">
                    через <a href="/api/find-company/by-email/">API «Компании по емейлу»</a>
                </span>
            </span>
        </div>
    </transition>
</div>
`;

export default {
    name: "EmailResults",
    template,
    data() {
        return {
            role: null,
            company: null,
            url: `${serviceUrl}/findByEmail/company`,
        };
    },
    props: {
        suggestion: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            default: "suggestion",
            validator(value) {
                return ["suggestion", "clean"].includes(value);
            },
        },
        readyCallback: {
            type: Function,
            default: () => {},
        },
    },
    methods: {
        async getComputedData() {
            try {
                let response;

                if (this.type === "clean" && this.suggestion.suggestions) {
                    // для формы стандартизации в suggestion может быть
                    // закеширован дефолтный результат, делать запрос не нужно
                    response = this.suggestion;
                } else {
                    const api = new ApiRequest(this.url, {
                        token,
                        csrf: CSRF_TOKEN,
                    });
                    const query = this.type === "suggestion"
                        ? this.suggestion.value
                        : this.suggestion.email;
                    response = await api.send({ query });
                }

                if (!response.suggestions || !response.suggestions.length) return;

                this.role = EMAIL_TYPES[response.suggestions[0].data.email.type];

                this.enrichFields(response);
            } catch (e) {
                console.error(e);
            }
        },
        enrichFields(response) {
            if (!response.suggestions[0].data.company) return;

            this.company = response.suggestions[0].data.company;
            this.company.okved = `${this.company.okved_name}
            <br/>(ОКВЭД — ${this.company.okved}) `;
            this.company.timezone = this.company.timezone.replace("UTC", "GMT");
            this.company.income = this.company.income
                ? `${utils.formatMoney(this.company.income)}`
                : FNS_DOESNT_NOW;
            this.company.employee_count = this.company.employee_count || FNS_DOESNT_NOW;
        },
    },
    mounted() {
        this.getComputedData()
            .then(() => {
                this.readyCallback();
            });
    },
    watch: {
        suggestion: {
            handler() {
                this.company = null;
                this.role = null;
                this.getComputedData()
                    .then(() => {
                        this.readyCallback();
                    });
            },
            deep: true,
        },
    },
};
